import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Loader2, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMyRosterEntriesQuery } from '@/roster/queries';
import { cn } from '@/lib/utils';
import { useCodexTree, useCodexSearch } from '../queries';
import { CodexTree } from '../components/CodexTree';
import { CodexContent } from '../components/CodexContent';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type { CodexCategoryTree, CodexEntryListItem } from '../types';
import '../codex.css';

const ALL_CHARACTERS = 'all';

/** Reads an integer route param, or undefined when absent. */
function getIntParam(searchParams: URLSearchParams, key: string): number | undefined {
  const value = searchParams.get(key);
  return value ? parseInt(value, 10) : undefined;
}

export function CodexPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  const categoryId = getIntParam(searchParams, 'category');
  const subjectId = getIntParam(searchParams, 'subject');
  const entryId = getIntParam(searchParams, 'entry');
  const characterId = getIntParam(searchParams, 'character');

  const { data: myCharacters } = useMyRosterEntriesQuery();
  const { data: tree, isLoading: treeLoading } = useCodexTree(characterId);
  const { data: searchResults, isLoading: searchLoading } = useCodexSearch(
    debouncedSearch,
    characterId
  );

  // Every navigation preserves the character scope.
  const updateParams = useCallback(
    (params: Record<string, string>) => {
      const character = searchParams.get('character');
      if (character) params.character = character;
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleSelectCategory = useCallback(
    (id: number) => {
      updateParams({ category: id.toString() });
      setSearchInput('');
    },
    [updateParams]
  );

  const handleSelectSubject = useCallback(
    (id: number) => {
      updateParams({ subject: id.toString() });
      setSearchInput('');
    },
    [updateParams]
  );

  const handleSelectEntry = useCallback(
    (id: number) => {
      const params: Record<string, string> = { entry: id.toString() };
      if (subjectId) params.subject = subjectId.toString();
      updateParams(params);
      setSearchInput('');
    },
    [updateParams, subjectId]
  );

  const handleNavigateBreadcrumb = useCallback(
    (type: 'home' | 'category' | 'subject', id?: number) => {
      if (type === 'home') {
        updateParams({});
        return;
      }
      if (id === undefined) return;
      updateParams({ [type]: id.toString() });
    },
    [updateParams]
  );

  const handleSelectCharacter = useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams);
      if (value === ALL_CHARACTERS) {
        params.delete('character');
      } else {
        params.set('character', value);
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const showSearchResults = debouncedSearch.length >= 2;
  // Two or more characters: the scope dropdown and the per-character badges both
  // answer "which of mine", and neither means anything with one (#4191).
  const multiCharacter = (myCharacters?.length ?? 0) >= 2;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-6 md:flex-row">
        {/* Sidebar: beside the content from md up, above it on a phone (#4198). */}
        <aside className="w-full shrink-0 md:w-64">
          <div className="space-y-4 md:sticky md:top-4">
            {/* Character knowledge scope (multi-character accounts only) */}
            {multiCharacter && (
              <Select
                value={characterId ? characterId.toString() : ALL_CHARACTERS}
                onValueChange={handleSelectCharacter}
              >
                <SelectTrigger aria-label="Character knowledge scope">
                  <Users className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_CHARACTERS}>All characters</SelectItem>
                  {myCharacters?.map((character) => (
                    <SelectItem key={character.id} value={character.id.toString()}>
                      {character.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search codex..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Search Results or Tree */}
            <CodexSidebarResults
              showSearchResults={showSearchResults}
              searchLoading={searchLoading}
              searchResults={searchResults}
              treeLoading={treeLoading}
              tree={tree}
              characterId={characterId}
              categoryId={categoryId}
              subjectId={subjectId}
              onSelectCategory={handleSelectCategory}
              onSelectSubject={handleSelectSubject}
              onSelectEntry={handleSelectEntry}
            />
          </div>
        </aside>

        {/* Main Content */}
        <main className="min-w-0 flex-1">
          <CodexContent
            categoryId={categoryId}
            subjectId={subjectId}
            entryId={entryId}
            characterId={characterId}
            multiCharacter={multiCharacter}
            onSelectSubject={handleSelectSubject}
            onSelectEntry={handleSelectEntry}
            onNavigateBreadcrumb={handleNavigateBreadcrumb}
          />
        </main>
      </div>
    </div>
  );
}

function CodexSidebarResults({
  showSearchResults,
  searchLoading,
  searchResults,
  treeLoading,
  tree,
  characterId,
  categoryId,
  subjectId,
  onSelectCategory,
  onSelectSubject,
  onSelectEntry,
}: {
  showSearchResults: boolean;
  searchLoading: boolean;
  searchResults: CodexEntryListItem[] | undefined;
  treeLoading: boolean;
  tree: CodexCategoryTree[] | undefined;
  characterId?: number;
  categoryId?: number;
  subjectId?: number;
  onSelectCategory: (id: number) => void;
  onSelectSubject: (id: number) => void;
  onSelectEntry: (id: number) => void;
}) {
  const renderSearchResults = () => {
    if (searchLoading) {
      return (
        <div className="flex items-center gap-2 py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Searching...</span>
        </div>
      );
    }
    if (searchResults?.length === 0) {
      return <div className="py-2 text-sm text-muted-foreground">No results found</div>;
    }
    return searchResults?.map((entry) => (
      <SearchResultItem key={entry.id} entry={entry} onClick={() => onSelectEntry(entry.id)} />
    ));
  };

  if (showSearchResults) {
    return (
      <div className="space-y-1">
        <div className="text-sm font-medium text-muted-foreground">Search Results</div>
        {renderSearchResults()}
      </div>
    );
  }

  if (treeLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="ml-4 h-6 w-3/4" />
        <Skeleton className="ml-4 h-6 w-3/4" />
      </div>
    );
  }

  if (!tree) return null;

  return (
    <CodexTree
      categories={tree}
      characterId={characterId}
      selectedCategoryId={categoryId}
      selectedSubjectId={subjectId}
      onSelectCategory={onSelectCategory}
      onSelectSubject={onSelectSubject}
    />
  );
}

function SearchResultItem({ entry, onClick }: { entry: CodexEntryListItem; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full rounded px-2 py-1.5 text-left hover:bg-accent',
        !entry.is_public && 'codex-restricted'
      )}
    >
      <div className="text-sm font-medium">{entry.name}</div>
      <div className="truncate text-xs text-muted-foreground">
        {entry.subject_path.map((s) => s.name).join(' > ')}
      </div>
    </button>
  );
}
